import * as React from 'react';

// Individual icons are available via: import { IconName } from '@superops/super-icons/icons'

// Note: We intentionally avoid importing `IconsList` here to prevent bundling all icons

// Statically imported icons (whitelist for left sidebar navigation icons)
// These icons are imported statically to avoid lazy loading overhead

// Work Management icons
import TicketModuleIcon from './icons/TicketModule.tsx';
import TaskModuleIcon from './icons/TaskModule.tsx';
import ProjectModuleIcon from './icons/ProjectModule.tsx';
import TimesheetModuleIcon from './icons/TimesheetModule.tsx';
import SchedulingModuleIcon from './icons/SchedulingModule.tsx';

// Asset Management icons
import AssetModuleIcon from './icons/AssetModule.tsx';
import AlertModuleIcon from './icons/AlertModule.tsx';
import PatchModuleIcon from './icons/PatchModule.tsx';
import ScriptModuleIcon from './icons/ScriptModule.tsx';
import NetworkMonitoringIcon from './icons/NetworkMonitoring.tsx';

// Network icons
import InternetIcon from './icons/Internet.tsx';
import SwitchIcon from './icons/Switch.tsx';
import WindowsIcon from './icons/Windows.tsx';
import FireWallLightIcon from './icons/FireWallLight.tsx';
import AboutIcon from './icons/About.tsx';
import UnknownDeviceIcon from './icons/UnknownDevice.tsx';
import LinuxIcon from './icons/LinuxIcon.tsx';
import MacIcon from './icons/Mac.tsx';
import PrinterIcon from './icons/Printer.tsx';
import RouterIcon from './icons/Router.tsx';
import RouterLightIcon from './icons/RouterLight.tsx';
import ServerHardwareControllerNewIcon from './icons/ServerHardwareControllerNew.tsx';
import ServerHardwareControllerIcon from './icons/ServerHardwareController.tsx';
import WirelessLanControllerNewIcon from './icons/WirelessLanControllerNew.tsx';
import WireLessAccessPointNewIcon from './icons/WireLessAccessPointNew.tsx';
import StorageIcon from './icons/Storage.tsx';
import IpCameraIcon from './icons/IpCamera.tsx';
import VoipIcon from './icons/Voip.tsx';
import LoadBalancerIcon from './icons/LoadBalancer.tsx';

// Client Management icons
import ClientModuleIcon from './icons/ClientModule.tsx';
import ContractModuleIcon from './icons/ContractModule.tsx';
import InvoiceAuditModuleIcon from './icons/InvoiceAuditModule.tsx';
import InvoiceModuleIcon from './icons/InvoiceModule.tsx';
import QuoteModuleIcon from './icons/QuoteModule.tsx';
import SalesInboxModuleIcon from './icons/SalesInboxModule.tsx';

// Documentation icons
import ItDocumentModuleIcon from './icons/ItDocumentModule.tsx';
import KnowledgeBaseModuleIcon from './icons/KnowledgeBaseModule.tsx';

// Color icons (to ensure DarkReader processes them correctly)
import TotalTimeLoggedIcon from './icons/TotalTimeLogged.tsx';

// Whitelist map for statically imported icons
const staticIconsWhitelist = new Map<string, React.ComponentType<any>>([
  // Work Management icons
  ['TicketModule', TicketModuleIcon],
  ['ticketModule', TicketModuleIcon],
  ['TaskModule', TaskModuleIcon],
  ['taskModule', TaskModuleIcon],
  ['ProjectModule', ProjectModuleIcon],
  ['projectModule', ProjectModuleIcon],
  ['TimesheetModule', TimesheetModuleIcon],
  ['timesheetModule', TimesheetModuleIcon],
  ['SchedulingModule', SchedulingModuleIcon],
  ['schedulingModule', SchedulingModuleIcon],

  // Asset Management icons
  ['AssetModule', AssetModuleIcon],
  ['assetModule', AssetModuleIcon],
  ['AlertModule', AlertModuleIcon],
  ['alertModule', AlertModuleIcon],
  ['PatchModule', PatchModuleIcon],
  ['patchModule', PatchModuleIcon],
  ['ScriptModule', ScriptModuleIcon],
  ['scriptModule', ScriptModuleIcon],
  ['NetworkMonitoring', NetworkMonitoringIcon],
  ['networkMonitoring', NetworkMonitoringIcon],

  // Network icons
  ['Internet', InternetIcon],
  ['Switch', SwitchIcon],
  ['windows', WindowsIcon],
  ['FireWallLight', FireWallLightIcon],
  ['About', AboutIcon],
  ['UnknownDevice', UnknownDeviceIcon],
  ['Linux', LinuxIcon],
  ['Mac', MacIcon],
  ['Printer', PrinterIcon],
  ['Router', RouterIcon],
  ['RouterLight', RouterLightIcon],
  ['ServerHardwareControllerNew', ServerHardwareControllerNewIcon],
  ['WirelessLanControllerNew', WirelessLanControllerNewIcon],
  ['WireLessAccessPointNew', WireLessAccessPointNewIcon],
  ['Storage', StorageIcon],
  ['ServerHardwareController', ServerHardwareControllerIcon],
  ['IpCamera', IpCameraIcon],
  ['Voip', VoipIcon],
  ['LoadBalancer', LoadBalancerIcon],

  // Client Management icons
  ['ClientModule', ClientModuleIcon],
  ['clientModule', ClientModuleIcon],
  ['ContractModule', ContractModuleIcon],
  ['contractModule', ContractModuleIcon],
  ['InvoiceAuditModule', InvoiceAuditModuleIcon],
  ['invoiceAuditModule', InvoiceAuditModuleIcon],
  ['InvoiceModule', InvoiceModuleIcon],
  ['invoiceModule', InvoiceModuleIcon],
  ['QuoteModule', QuoteModuleIcon],
  ['quoteModule', QuoteModuleIcon],
  ['SalesInboxModule', SalesInboxModuleIcon],
  ['salesInboxModule', SalesInboxModuleIcon],

  // Documentation icons
  ['ItDocumentModule', ItDocumentModuleIcon],
  ['itDocumentModule', ItDocumentModuleIcon],
  ['KnowledgeBaseModule', KnowledgeBaseModuleIcon],
  ['knowledgeBaseModule', KnowledgeBaseModuleIcon],

  // Color icons (statically imported for DarkReader compatibility)
  ['TotalTimeLogged', TotalTimeLoggedIcon],
  ['totalTimeLogged', TotalTimeLoggedIcon]
]);

// Simple cache to store loaded icon components
const iconCache = new Map<string, React.ComponentType<any>>();

type Size = 'xs' | 'sm' | 'md' | 'lg' | 'slg' | 'xl' | 'xxl' | 'xxxl';

// Backward compatible type export, relaxed to string to avoid importing IconsList for keys
export type IconNameType = string;

interface Props {
  name: IconNameType;
  color?: string;
  size?: Size;
  bgFillColor?: string;
}

// Map problematic icon names to safe file names
const getIconFileName = (iconName: string): string => {
  const iconMap: Record<string, string> = {
    delete: 'deleteIcon',
    Delete: 'deleteIcon',
    add: 'Add',
    Add: 'Add',
    edit: 'Edit',
    Edit: 'Edit',
    FireWall: 'Firewall',
    Firewall: 'Firewall',
    HourGlassColor: 'HourglassSimple',
    HourglassColor: 'HourglassColor',
    hourglassColor: 'HourglassColor'
  };
  return iconMap[iconName] || iconName;
};

// Separate component for lazy-loaded icons
const LazyIcon = ({
  fileName,
  name,
  color,
  size,
  bgFillColor
}: {
  fileName: string;
  name: string;
  color: string;
  size: Size;
  bgFillColor: string;
}) => {
  const [IconComponent, setIconComponent] =
    React.useState<React.ComponentType<any> | null>(() => {
      // Check cache immediately on mount
      return iconCache.get(fileName) || null;
    });
  const [isLoading, setIsLoading] = React.useState(!iconCache.has(fileName));

  React.useEffect(() => {
    // If already cached, update component state and return
    if (iconCache.has(fileName)) {
      const cachedComponent = iconCache.get(fileName);
      setIconComponent(() => cachedComponent);
      setIsLoading(false);
      return;
    }

    // Load the icon dynamically
    const loadIcon = async () => {
      try {
        const mod = await import(`./icons/${fileName}`);
        const component = mod.default;

        // Cache it for future use
        iconCache.set(fileName, component);

        // Set the component
        setIconComponent(() => component);
        setIsLoading(false);
      } catch (error) {
        console.warn(
          `Failed to load icon: ${name}, falling back to About icon`,
          error
        );
        // Check if About icon is already cached
        if (iconCache.has('About')) {
          const aboutComponent = iconCache.get('About');
          setIconComponent(() => aboutComponent);
          setIsLoading(false);
        } else {
          // Load About icon as fallback
          import('./icons/About.js')
            .then((mod) => {
              const component = mod.default;
              iconCache.set('About', component);
              setIconComponent(() => component);
              setIsLoading(false);
            })
            .catch(() => {
              // If About icon also fails, just stop loading
              setIsLoading(false);
            });
        }
      }
    };

    loadIcon();
  }, [fileName, name]);

  // Create dimensions for the invisible placeholder
  const getSizeStyles = (iconSize: Size) => {
    switch (iconSize) {
      case 'xs':
        return { width: '12px', height: '12px' };
      case 'sm':
        return { width: '16px', height: '16px' };
      case 'md':
        return { width: '20px', height: '20px' };
      case 'lg':
        return { width: '24px', height: '24px' };
      case 'slg':
        return { width: '32px', height: '32px' };
      case 'xl':
        return { width: '38px', height: '38px' };
      case 'xxl':
        return { width: '70px', height: '70px' };
      case 'xxxl':
        return { width: '140px', height: '140px' };
      default:
        return {};
    }
  };

  // Show invisible placeholder only when loading new icons
  if (isLoading || !IconComponent) {
    return (
      <span
        style={{
          ...getSizeStyles(size),
          display: 'inline-block',
          visibility: 'hidden'
        }}
        aria-hidden="true"
      />
    );
  }

  // Render the cached or loaded icon directly
  return <IconComponent color={color} size={size} bgFillColor={bgFillColor} />;
};

export const SVGIcon = ({
  name,
  color = 'black',
  size = 'md',
  bgFillColor = 'black'
}: Props) => {
  // Use About as fallback if name is not supplied or empty (mimics old implementation behavior)
  let iconName: string;

  // Handle arrays: take the first item as a string
  let processedName = name;
  if (Array.isArray(name)) {
    processedName = name.length > 0 ? String(name[0]) : '';
  }

  if (
    !processedName ||
    typeof processedName !== 'string' ||
    processedName.trim() === ''
  ) {
    iconName = 'About';
  } else {
    iconName = processedName;
  }
  const fileName = getIconFileName(iconName);
  // Check static whitelist first - render synchronously without state
  const staticIcon = staticIconsWhitelist.get(fileName);
  if (staticIcon) {
    const IconComponent = staticIcon;
    return (
      <IconComponent color={color} size={size} bgFillColor={bgFillColor} />
    );
  }

  // For non-whitelisted icons, use lazy loading with state
  return (
    <LazyIcon
      fileName={fileName}
      name={name}
      color={color}
      size={size}
      bgFillColor={bgFillColor}
    />
  );
};

// Icon names are available via: import { iconNames, IconKeys } from '@superops/super-icons/iconNames'
